"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

type FullscreenImageProps = {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  sizes?: string;
  priority?: boolean;
};

export function FullscreenImage({
  src,
  alt,
  className = "",
  imageClassName = "object-cover",
  sizes,
  priority,
}: FullscreenImageProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`relative block cursor-zoom-in overflow-hidden p-0 ${className}`}
        aria-label={`View ${alt} full size`}
      >
        <Image
          src={src}
          alt={alt}
          fill
          className={imageClassName}
          sizes={sizes}
          priority={priority}
        />
      </button>
      {open && mounted
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center p-4"
              role="dialog"
              aria-modal="true"
              aria-label={alt}
            >
              <div
                className="absolute inset-0 bg-stone-900/90 backdrop-blur-sm"
                aria-hidden
                onClick={close}
              />
              <button
                type="button"
                onClick={close}
                className="absolute right-4 top-4 z-10 cursor-pointer rounded-full bg-stone-900/60 p-2 text-white transition hover:bg-stone-900/80"
                aria-label="Close image"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-6 w-6"
                  aria-hidden
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              <div className="relative z-10 h-full w-full max-h-[90vh] max-w-[90vw]">
                <Image
                  src={src}
                  alt={alt}
                  fill
                  className="object-contain"
                  sizes="90vw"
                  priority
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
